import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import type { AiChatTurn } from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import { Box } from "@mui/material";
import { Fragment } from "react";
import {
  resolveSlot,
  useExecutionPanelContext,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { Turn } from "./Turn";
import { ThoughtProcessGroup } from "./AssistantMessage/ThoughtProcessGroup";
import { isRenderableTextPart } from "./part-processor";
import { isWorkLogToolCall } from "./AssistantMessage/conversationTools";

/** Whether the turn already shows the merchant a reply. */
const turnHasAssistantText = (turn: AiChatTurn | undefined) =>
  !!turn?.nodes.some((node) =>
    node.messages.some(
      (message) =>
        message.role === "assistant" &&
        message.parts.some(
          (part) => part.type === "text" && isRenderableTextPart(part)
        )
    )
  );

const turnHasThoughtProcessStep = (
  turn: AiChatTurn | undefined,
  reasoningNodeAllowList: string[] | undefined,
  conversationTools: string[] | undefined
) => {
  if (!turn) {
    return false;
  }

  return turn.nodes.some((node) =>
    node.messages.some((message) =>
      message.parts.some((part) => {
        if (part.type === "dynamic-tool") {
          return isWorkLogToolCall(part, conversationTools);
        }

        if (part.type !== "reasoning" || node.type === "condition") {
          return false;
        }

        return (
          !reasoningNodeAllowList?.length ||
          reasoningNodeAllowList.includes(node.type ?? "")
        );
      })
    )
  );
};

export const TurnStream = () => {
  const {
    aiChat,
    streaming,
    debugMode,
    compact,
    showSystemMessages,
    showToolCallOutput,
    showToolsAsTasks,
    showToolsAsSteps,
    showConditionNodes,
    showReasoningMessages,
    showExpandedReasoningMessages,
    reasoningNodeAllowList,
    toolCallDefaultExpanded,
    disableResponseWordByWordLoading,
    clearReasoningOnMessage,
    hiddenTools,
    conversationTools,
    transformMessage,
    renderPart,
    layoutDensity,
    getSlot,
  } = useExecutionPanelContext();
  const turnFooter = getSlot("turnFooter");

  const turns = aiChat.turns;
  const activeTurnHasThoughtProcessStep = turnHasThoughtProcessStep(
    turns.at(-1),
    reasoningNodeAllowList,
    conversationTools
  );
  // A chat that logs its work never shows the loading gem (Figma "Atlas /
  // Work-log", ENG-7654): between the send and the first step the log's own
  // run-start state stands in — the "Thought process…" header, shimmering —
  // and it gives way to the real log, or to the reply, the moment either lands.
  const showRunStart =
    streaming &&
    !!showToolsAsSteps &&
    !activeTurnHasThoughtProcessStep &&
    !turnHasAssistantText(turns.at(-1));
  const showLoadingGem = streaming && !showToolsAsSteps;

  // Message-rendering flags forwarded to each turn (and on to its parts). The
  // debug toggle is the derived `debugMode`, not the `debug` message flag.
  const messageFlags = {
    isStreaming: streaming,
    debug: debugMode,
    showSystemMessages,
    showToolCallOutput,
    showToolsAsTasks,
    showToolsAsSteps,
    showConditionNodes,
    showReasoningMessages,
    showExpandedReasoningMessages,
    reasoningNodeAllowList,
    toolCallDefaultExpanded,
    disableResponseWordByWordLoading,
    clearReasoningOnMessage,
    hiddenTools,
    conversationTools,
    layoutDensity,
  };

  // Renders directly into the parent's single ConversationContent
  // (StickToBottom.Content) — must not wrap its own, or the content refs nest
  // and auto-scroll breaks.
  return (
    <>
      {turns.map((turn, idx) => {
        const isLastTurn = idx === turns.length - 1;
        return (
          <Fragment key={idx}>
            <Turn
              turn={turn}
              showVisibilityReasons={true}
              isLastTurn={isLastTurn}
              transformMessage={transformMessage}
              renderPart={renderPart}
              {...messageFlags}
            />
            {isLastTurn && showRunStart ? (
              <ThoughtProcessGroup
                parts={[]}
                isLastTurn
                isTrailing
                isStreaming
              />
            ) : null}
            {resolveSlot(turnFooter, {
              turn,
              streaming: streaming && isLastTurn,
            })}
          </Fragment>
        );
      })}
      {showLoadingGem ? (
        <Box
          sx={{
            width: compact ? "72px" : "100px",
            marginLeft: compact ? "-18px" : "-30px",
          }}
        >
          <DotLottieReact
            aria-label="AI is responding"
            role="status"
            src={"/lottie/blue-diamond.json"}
            loop={true}
            autoplay={true}
            renderConfig={{
              autoResize: true,
            }}
          />
        </Box>
      ) : null}
    </>
  );
};
